import {
  Box,
  Button,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { Image, styles } from "./styles";
import { MasteringPresets, PreviewProps } from "./types";
import { useEffect, useState } from "react";
import { Waveform } from "@dolbyio/media-uikit-react";
import { hideVolumeControl } from "../../utils/DolbyIoUtils";
import { APP_COLOR, TEXT_COLOR } from "../../theme";

export const Preview: React.FC<PreviewProps> = ({
  track,
  previewUrl,
  masteredMusicUrl,
  selectedPresetArray,
  setSelectedPresetValue,
  loading,
  previewState,
  handleAccessStage,
  previewStartValue,
  duration,
  setSelectedPreset,
}) => {
  const [selectedMusicType, setSelectedMusicType] = useState("original");

  useEffect(() => {
    hideVolumeControl();
    if (handleAccessStage) handleAccessStage(3);
  }, [handleAccessStage]);

  const getSelectedPreset = (selectedPresetValue: string) => {
    if (
      selectedPresetValue !== undefined &&
      selectedPresetValue !== "original"
    ) {
      const value = MasteringPresets.filter(
        (el) => el.value === selectedPresetValue
      );
      return value[0].title;
    }
    return "--";
  };

  const handleMusicTypeChange = (presetValue: string) => {
    if (!loading) {
      setSelectedPresetValue(presetValue);
      setSelectedMusicType(presetValue);
      if (setSelectedPreset) setSelectedPreset(getSelectedPreset(presetValue));
    }
  };

  useEffect(() => {
    if (!loading && previewUrl) {
      const presetValue = MasteringPresets.find(
        (el) => el.value === previewUrl.keys().next().value
      )?.value;
      if (presetValue) {
        setSelectedPresetValue(presetValue);
        setSelectedMusicType(presetValue);
        if (setSelectedPreset)
          setSelectedPreset(getSelectedPreset(presetValue));
      }
    }
  }, [loading, previewUrl, setSelectedPreset, setSelectedPresetValue]);

  return (
    <>
      <Box sx={{ display: "flex", gap: "20px", color: TEXT_COLOR }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Image
            style={styles.trackImage}
            src={
              track.track?.getImageUrl() !== undefined &&
              track.track?.getImageUrl().includes("http")
                ? track.track?.getImageUrl()
                : `/assets/images/logo-icon.png`
            }
            alt="TrackImage"
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box>
            <Box sx={styles.trackDetailsContainer}>
              <Box sx={{ width: "50px" }}>
                <Typography style={{ fontWeight: "bold" }}>Title</Typography>
              </Box>
              <Typography>{track.track?.getTitle()}</Typography>
            </Box>
            <Box sx={styles.trackDetailsContainer}>
              <Box sx={{ width: "50px" }}>
                <Typography style={{ fontWeight: "bold" }}>Artist</Typography>
              </Box>
              <Typography> {track.track?.getArtist()}</Typography>
            </Box>
            <Box sx={styles.trackDetailsContainer}>
              <Box sx={{ width: "50px" }}>
                <Typography style={{ fontWeight: "bold" }}>Genre</Typography>
              </Box>
              <Typography>
                {track.track
                  ?.getSongMetadata()
                  ?.getGenre()
                  .split(" ")
                  .join(", ")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <hr />
      <Box sx={{ display: "flex", justifyContent: "center", gap: "20px" }}>
        <Button
          onClick={() => setSelectedMusicType("original")}
          sx={
            selectedMusicType === "original"
              ? { color: APP_COLOR }
              : { color: TEXT_COLOR }
          }
        >
          Original
        </Button>
        {selectedPresetArray.length === 0 && (
          <Button
            onClick={() => setSelectedMusicType("master")}
            sx={
              selectedMusicType === "master" && !loading
                ? {
                    border: `1px solid ${APP_COLOR}`,
                    background: APP_COLOR,
                    color: TEXT_COLOR,
                  }
                : { color: TEXT_COLOR }
            }
          >
            Master
          </Button>
        )}
        {!masteredMusicUrl &&
          selectedPresetArray.map((presetValue) => {
            return (
              <Tooltip
                title={`${
                  MasteringPresets.find((el) => el.value === presetValue)
                    ?.description
                }  [${MasteringPresets.find(
                  (el) => el.value === presetValue
                )?.suggestedUse.join(", ")}]`}
                key={presetValue}
              >
                <Button
                  key={presetValue}
                  onClick={() => handleMusicTypeChange(presetValue)}
                  sx={
                    selectedMusicType === presetValue
                      ? {
                          border: `1px solid ${APP_COLOR}`,
                          background: APP_COLOR,
                          color: TEXT_COLOR,
                        }
                      : { color: TEXT_COLOR }
                  }
                >
                  {
                    MasteringPresets.find((el) => el.value === presetValue)
                      ?.title
                  }
                </Button>
              </Tooltip>
            );
          })}
      </Box>
      {loading ? (
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <LinearProgress sx={{ width: "80%", margin: "25px 0 25px 0" }} />
          </Box>
          <p>{previewState}</p>
        </Box>
      ) : (
        <Box
          sx={{
            maxWidth: "95%",
            height: previewUrl || masteredMusicUrl ? "260px" : "0",
          }}
        >
          {selectedMusicType === "original" && (
            <Waveform
              url={track.trackUrl}
              hasLoopControl={false}
              previewActive={!masteredMusicUrl}
              isPreviewDraggable={false}
              isPreviewResizable={false}
              preview={
                masteredMusicUrl
                  ? undefined
                  : {
                      start: previewStartValue ? previewStartValue : 0,
                      end:
                        previewStartValue! + duration! === 0
                          ? 30
                          : previewStartValue! + duration!,
                    }
              }
            />
          )}
          {masteredMusicUrl && selectedMusicType === "master" && (
            <Waveform
              url={masteredMusicUrl}
              hasLoopControl={false}
              previewActive={false}
              waveProgressColor="rgb(39, 182, 203)"
            />
          )}
          {!masteredMusicUrl &&
            selectedPresetArray.map((presetValue) => {
              if (
                selectedMusicType === presetValue &&
                previewUrl !== undefined
              ) {
                return (
                  <Waveform
                    key={presetValue}
                    url={previewUrl?.get(presetValue) || ""}
                    hasLoopControl={false}
                    previewActive={false}
                    waveProgressColor="rgb(39, 182, 203)"
                  />
                );
              } else {
                return (
                  <Box
                    key={presetValue}
                    sx={{ margin: "25px", textAlign: "center" }}
                  ></Box>
                );
              }
            })}
        </Box>
      )}
    </>
  );
};
